import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase, q } from '../lib/supabase';
import { useAsync } from '../lib/useAsync';
import { useGroup } from './GroupLayout';
import { computeBaseOverall, effectiveRating } from '../lib/balance';
import { replayRatings } from '../lib/ratings';
import { aggregateRatings, applyPeerRatings } from '../lib/peerRatings';
import { resolveMotm } from '../lib/motm';
import { attrsFor, GK_ATTR, POSITION_LABELS } from '../lib/constants';
import { ErrorBanner, Spinner } from '../components/Shared';
import Icon from '../components/Icon';

/** A rating line over time. Small enough to read at a glance, honest about scale. */
function Sparkline({ points }) {
  if (points.length < 2) return null;

  const W = 300;
  const H = 64;
  const PAD = 4;
  const values = points.map((p) => p.rating);
  const lo = Math.min(...values) - 0.3;
  const hi = Math.max(...values) + 0.3;
  const span = hi - lo || 1;

  const x = (i) => PAD + (i * (W - PAD * 2)) / (points.length - 1);
  const y = (v) => PAD + (H - PAD * 2) * (1 - (v - lo) / span);

  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.rating).toFixed(1)}`).join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(1)},${H - PAD} L${PAD},${H - PAD} Z`;

  return (
    <svg className="spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Rating over time">
      <path d={area} className="spark__area" />
      <path d={line} className="spark__line" />
      {points.map((p, i) => (
        <circle
          key={p.gameId}
          cx={x(i)}
          cy={y(p.rating)}
          r={i === points.length - 1 ? 5 : 3.4}
          className={`spark__dot spark__dot--${p.outcome > 0 ? 'win' : p.outcome < 0 ? 'loss' : 'draw'}`}
        />
      ))}
    </svg>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="stat">
      <span className="stat__value">{value}</span>
      <span className="stat__label">{label}</span>
      {sub && <span className="stat__sub">{sub}</span>}
    </div>
  );
}

export default function PlayerScreen() {
  const { group } = useGroup();
  const { memberId } = useParams();
  const navigate = useNavigate();

  const { data, loading, error, reload } = useAsync(async () => {
    const members = await q(
      supabase
        .from('group_members')
        .select('id, profile_id, role, positions, attributes, form, rating_offset, display_name, profiles(display_name)')
        .eq('group_id', group.id),
      'Loading the squad'
    );

    const named = (members || []).map((m) => ({
      ...m,
      name: (m.profile_id ? m.profiles?.display_name : null) || m.display_name || 'Unnamed',
    }));
    const me = named.find((m) => m.id === memberId);
    if (!me) throw new Error('That player is not in this group.');

    const games = await q(
      supabase
        .from('games')
        .select('id, score_a, score_b, created_at, man_of_the_match_id')
        .eq('group_id', group.id)
        .eq('status', 'completed')
        .order('created_at', { ascending: true }),
      'Loading past games'
    );

    let participants = [];
    let votes = [];
    if (games?.length) {
      const ids = games.map((g) => g.id);
      participants = await q(
        supabase
          .from('game_participants')
          .select('game_id, member_id, team, position, goals, assists, performance')
          .in('game_id', ids),
        'Loading past results'
      );
      const { data: voteRows } = await supabase
        .from('honours')
        .select('game_id, recipient_member_id')
        .in('game_id', ids)
        .eq('honour_type', 'motm');
      votes = voteRows || [];
    }

    const byGame = {};
    (participants || []).forEach((p) => {
      (byGame[p.game_id] ||= []).push({
        memberId: p.member_id,
        team: p.team,
        position: p.position,
        goals: p.goals || 0,
        assists: p.assists || 0,
        performance: p.performance ?? null,
      });
    });
    let fullGames = (games || []).map((g) => ({ ...g, participants: byGame[g.id] || [] }));

    if (games?.length) {
      const { data: peer } = await supabase
        .from('player_ratings')
        .select('game_id, subject_member_id, score')
        .in('game_id', games.map((g) => g.id));
      fullGames = applyPeerRatings(fullGames, aggregateRatings(peer || []));
    }

    const state = replayRatings(named, fullGames);

    return { me, squad: named, games: fullGames, votes, state };
  }, [group.id, memberId]);

  const summary = useMemo(() => {
    if (!data) return null;
    const { me, games, votes, state } = data;

    let played = 0;
    let wins = 0;
    let draws = 0;
    let goals = 0;
    let assists = 0;
    let motm = 0;
    const positions = {};
    const recent = [];

    games.forEach((g) => {
      const mine = g.participants.find((p) => p.memberId === me.id);
      if (!mine) return;
      played += 1;
      goals += mine.goals;
      assists += mine.assists;
      positions[mine.position] = (positions[mine.position] || 0) + 1;

      const diff = (g.score_a ?? 0) - (g.score_b ?? 0);
      const outcome = diff === 0 ? 0 : (mine.team === 'A') === diff > 0 ? 1 : -1;
      if (outcome === 1) wins += 1;
      if (outcome === 0) draws += 1;

      const winner = resolveMotm({
        votes: votes.filter((v) => v.game_id === g.id),
        adminPickId: g.man_of_the_match_id,
      });
      if (winner.memberId === me.id) motm += 1;

      recent.push({
        id: g.id,
        at: g.created_at,
        outcome,
        forText: mine.team === 'A' ? `${g.score_a}–${g.score_b}` : `${g.score_b}–${g.score_a}`,
        position: mine.position,
        goals: mine.goals,
        assists: mine.assists,
      });
    });

    const topPosition = Object.entries(positions).sort((a, b) => b[1] - a[1])[0];

    return {
      played,
      wins,
      draws,
      losses: played - wins - draws,
      goals,
      assists,
      motm,
      topPosition: topPosition ? topPosition[0] : null,
      recent: recent.reverse(),
      history: state[me.id]?.history || [],
    };
  }, [data]);

  if (loading) return <div className="page"><Spinner label="Loading player…" /></div>;

  if (error) {
    return (
      <div className="page">
        <ErrorBanner error={error} onRetry={reload} />
        <button type="button" className="btn btn--outline" onClick={() => navigate(`/g/${group.id}/squad`)}>
          Back to squad
        </button>
      </div>
    );
  }

  const { me } = data;
  const base = computeBaseOverall(me.attributes);
  const offset = Number(me.rating_offset) || 0;
  const form = Number(me.form) || 0;
  const rating = effectiveRating(me);

  return (
    <div className="page">
      <Link to={`/g/${group.id}/squad`} className="backlink">
        <Icon name="chevronLeft" size={15} />
        Squad
      </Link>

      <header className="profile__head">
        <div className="profile__rating" title={`Attributes ${base.toFixed(1)}, results ${offset >= 0 ? '+' : ''}${offset.toFixed(2)}, form ${form >= 0 ? '+' : ''}${form.toFixed(2)}`}>
          {rating.toFixed(1)}
        </div>
        <div>
          <h1 className="page__title">{me.name}</h1>
          <p className="page__sub">
            {me.positions?.length ? me.positions.map((p) => POSITION_LABELS[p] || p).join(' · ') : 'No position set'}
          </p>
        </div>
      </header>

      <section className="card">
        <h2 className="team-heading">WHERE THE RATING COMES FROM</h2>
        <ul className="breakdown">
          <li>
            <span>Attributes</span>
            <em>{base.toFixed(2)}</em>
            <small>your assessment — never changed automatically</small>
          </li>
          <li>
            <span>Results</span>
            <em className={offset > 0.01 ? 'up' : offset < -0.01 ? 'down' : ''}>
              {offset >= 0 ? '+' : ''}
              {offset.toFixed(2)}
            </em>
            <small>what the scorelines have said since</small>
          </li>
          <li>
            <span>Form</span>
            <em className={form > 0.01 ? 'up' : form < -0.01 ? 'down' : ''}>
              {form >= 0 ? '+' : ''}
              {form.toFixed(2)}
            </em>
            <small>recent streak — decays every week</small>
          </li>
          <li className="breakdown__total">
            <span>Rating</span>
            <em>{rating.toFixed(2)}</em>
            <small>{rating === 10 || rating === 1 ? 'clamped to the 1–10 range' : ''}</small>
          </li>
        </ul>
      </section>

      {summary.history.length >= 2 && (
        <section className="card">
          <h2 className="team-heading">RATING OVER TIME</h2>
          <Sparkline points={summary.history} />
          <p className="field__note">
            One point per game played. Green won, red lost, grey drew.
          </p>
        </section>
      )}

      <div className="stats">
        <Stat label="Played" value={summary.played} />
        <Stat
          label="Win rate"
          value={summary.played ? `${Math.round((summary.wins / summary.played) * 100)}%` : '–'}
          sub={`${summary.wins}W ${summary.draws}D ${summary.losses}L`}
        />
        <Stat label="Goals" value={summary.goals} />
        <Stat label="Assists" value={summary.assists} />
        <Stat label="MOTM" value={summary.motm} />
        <Stat label="Usual spot" value={summary.topPosition || '–'} />
      </div>

      <section>
        <h2 className="team-heading">ATTRIBUTES</h2>
        <div className="bars">
          {attrsFor(me.positions).map((a) => {
            const v = me.attributes?.[a.key] ?? 5;
            return (
              <div key={a.key} className={`bar ${a.key === GK_ATTR.key ? 'bar--gk' : ''}`}>
                <span className="bar__label">{a.label}</span>
                <span className="bar__track">
                  <span className="bar__fill" style={{ width: `${v * 10}%` }} />
                </span>
                <span className="bar__value">{v}</span>
              </div>
            );
          })}
        </div>
      </section>

      {summary.recent.length > 0 && (
        <section style={{ marginTop: 26 }}>
          <h2 className="team-heading">RECENT GAMES</h2>
          <ul className="recent">
            {summary.recent.slice(0, 12).map((r) => (
              <li key={r.id} className={`recent__row recent__row--${r.outcome > 0 ? 'w' : r.outcome < 0 ? 'l' : 'd'}`}>
                <span className="recent__badge">{r.outcome > 0 ? 'W' : r.outcome < 0 ? 'L' : 'D'}</span>
                <span className="recent__score">{r.forText}</span>
                <span className="recent__pos">{r.position}</span>
                <span className="recent__stats">
                  {r.goals > 0 && <span>⚽{r.goals}</span>}
                  {r.assists > 0 && <span>🅰️{r.assists}</span>}
                </span>
                <span className="recent__date">
                  {new Date(r.at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
